// ── Curated tip-token allowlist ──────────────────────────────────
//
// A small, hand-picked set of CashTokens that users can tip and send inside
// BCHNostr. This is deliberately NOT "any token" — an open list would fill
// the picker with scam look-alikes. Every entry here is a token the operator
// has chosen to surface.
//
// For each token we need its on-chain CATEGORY ID and DECIMALS to build a
// valid transfer, plus a live USD PRICE to let users tip in dollars. Rather
// than hardcode hex category IDs for every token (error-prone, and a wrong
// digit sends funds nowhere), we resolve them live from the same trusted
// Cauldron indexer the app already uses for its trending-token cards — keyed
// by ticker symbol. PUSD is the exception: it is a stablecoin we anchor by
// its known, verified category so it never depends on an active DEX pool.
//
// SECURITY: resolution is read-only HTTP (see src/lib/cauldron.ts). The
// category a token resolves to is only ever used to construct a transfer the
// USER explicitly initiates; it is never a trust/authz boundary.

import { resolveTokenBySymbol, resolveTokenById, cauldronSwapUrl, tokenIconUrl, type CauldronToken } from './cauldron';
import { PUSD_CATEGORY, PUSD_DECIMALS } from './pusd';

/** A token the user can tip/send. */
export interface TipToken {
  /** Ticker, e.g. "PUSD". Used as the stable UI key. */
  symbol: string;
  /** Human name, e.g. "ParyonUSD". */
  name: string;
  /** On-chain genesis category ID (64-char hex), or null until resolved. */
  category: string | null;
  /** Token decimals (base units = 10^decimals per whole token). */
  decimals: number;
  /** Live USD price per whole token, or null if unavailable. */
  priceUsd: number | null;
  /** Icon URL (Riften metadata host). */
  iconUrl: string;
  /** True for ~$1 stablecoins — the UI treats $amount as the token amount. */
  stable: boolean;
  /** Cauldron swap page so users who don't hold it can acquire it. */
  swapUrl: string;
}

// ── The allowlist ────────────────────────────────────────────────
// `anchorCategory` is only set for tokens we pin to a known category (PUSD).
// Everything else resolves live by ticker from Cauldron.
interface TipTokenSeed {
  symbol: string;
  name: string;
  stable: boolean;
  anchorCategory?: string;
  anchorDecimals?: number;
}

const SEEDS: TipTokenSeed[] = [
  { symbol: 'PUSD', name: 'ParyonUSD', stable: true, anchorCategory: PUSD_CATEGORY, anchorDecimals: PUSD_DECIMALS },
  { symbol: 'MUSD', name: 'MayaUSD', stable: true },
  { symbol: 'RES', name: 'RES', stable: false },
  { symbol: 'LIFT', name: 'LIFT', stable: false },
  { symbol: 'FURU', name: 'FURU', stable: false },
];

/** The ordered list of allowlisted tip-token tickers. */
export const TIP_TOKEN_SYMBOLS = SEEDS.map(s => s.symbol);

function seedToToken(seed: TipTokenSeed, resolved: CauldronToken | null): TipToken {
  const category = seed.anchorCategory ?? resolved?.tokenId ?? null;
  return {
    symbol: seed.symbol,
    name: resolved?.name || seed.name,
    category,
    decimals: seed.anchorDecimals ?? (resolved?.decimals ?? 0),
    priceUsd: resolved?.priceUsd && resolved.priceUsd > 0 ? resolved.priceUsd : (seed.stable ? 1 : null),
    iconUrl: category ? tokenIconUrl(category) : (resolved?.iconUrl || ''),
    stable: seed.stable,
    swapUrl: category ? cauldronSwapUrl(category) : 'https://app.cauldron.quest',
  };
}

// ── Resolution (cached) ──────────────────────────────────────────
// Resolve all allowlisted tokens to concrete {category, decimals, price}.
// Cached in-memory for the session; individual Cauldron lookups have their
// own TTL cache, so this stays cheap on repeat calls.

let _resolvedCache: TipToken[] | null = null;
let _resolvePromise: Promise<TipToken[]> | null = null;

/**
 * Resolve the full allowlist. Tokens that fail to resolve (no Cauldron pool,
 * network error) are still returned, but with `category: null` — callers must
 * skip those for sending. Stablecoins anchored by category always resolve.
 */
export async function resolveTipTokens(force = false): Promise<TipToken[]> {
  if (!force && _resolvedCache) return _resolvedCache;
  if (_resolvePromise) return _resolvePromise;

  _resolvePromise = (async () => {
    const out = await Promise.all(SEEDS.map(async seed => {
      // Anchored tokens (PUSD): still try to fetch a live price by id, but the
      // category/decimals are fixed regardless of the lookup succeeding.
      try {
        if (seed.anchorCategory) {
          const byId = await resolveTokenById(seed.anchorCategory).catch(() => null);
          return seedToToken(seed, byId);
        }
        const bySym = await resolveTokenBySymbol(seed.symbol).catch(() => null);
        return seedToToken(seed, bySym);
      } catch {
        return seedToToken(seed, null);
      }
    }));
    _resolvedCache = out;
    _resolvePromise = null;
    return out;
  })();

  return _resolvePromise;
}

/** Synchronously read the last resolved allowlist (may be null before first resolve). */
export function getResolvedTipTokens(): TipToken[] | null {
  return _resolvedCache;
}

/** Find a resolved token by symbol (case-insensitive). */
export function findTipToken(symbol: string): TipToken | null {
  const list = _resolvedCache;
  if (!list) return null;
  return list.find(t => t.symbol.toLowerCase() === symbol.toLowerCase()) ?? null;
}

/** Find a resolved token by on-chain category id (case-insensitive). */
export function findTipTokenByCategory(category: string): TipToken | null {
  const list = _resolvedCache;
  if (!list || !category) return null;
  const c = category.toLowerCase();
  return list.find(t => t.category?.toLowerCase() === c) ?? null;
}

// ── Amount conversion ────────────────────────────────────────────

/** 10^decimals as a bigint (base units per whole token). */
export function unitFactor(decimals: number): bigint {
  return BigInt(10) ** BigInt(Math.max(0, Math.floor(decimals)));
}

/**
 * Convert a USD tip amount into token BASE UNITS for a given token.
 * Stablecoins use $1 = 1 token; others divide by the live USD price.
 * Returns 0n if the price is unknown (caller should block the tip).
 */
export function usdToTokenBaseUnits(usd: number, token: TipToken): bigint {
  if (!Number.isFinite(usd) || usd <= 0) return BigInt(0);
  const price = token.stable ? 1 : token.priceUsd;
  if (!price || price <= 0) return BigInt(0);
  const whole = usd / price; // whole tokens
  const factor = Number(unitFactor(token.decimals));
  return BigInt(Math.round(whole * factor));
}

/** Convert token base units back to a whole-token number for display. */
export function baseUnitsToWhole(base: bigint, decimals: number): number {
  return Number(base) / Number(unitFactor(decimals));
}

/**
 * Convert a whole-token amount (as typed by a user) into BASE UNITS.
 * Rounds to the token's decimal precision. Returns 0n for invalid input.
 */
export function wholeToBaseUnits(whole: number, token: TipToken): bigint {
  if (!Number.isFinite(whole) || whole <= 0) return BigInt(0);
  const factor = Number(unitFactor(token.decimals));
  return BigInt(Math.round(whole * factor));
}

/** Format a token base-unit amount for display, e.g. "12.50 RES". */
export function formatTokenAmount(base: bigint, token: TipToken): string {
  const whole = baseUnitsToWhole(base, token.decimals);
  // Stablecoins read naturally as dollars; others as token units.
  const dp = token.decimals === 0 ? 0 : Math.min(token.decimals, token.stable ? 2 : 4);
  return `${whole.toFixed(dp)} ${token.symbol}`;
}

/** Best-effort USD value of a token base-unit balance (null if no price). */
export function tokenBalanceUsd(base: bigint, token: TipToken): number | null {
  const price = token.stable ? 1 : token.priceUsd;
  if (!price || price <= 0) return null;
  return baseUnitsToWhole(base, token.decimals) * price;
}

// ── Tip-receipt display ──────────────────────────────────────────

/** Minimal shape of a parsed tip receipt (matches useNotifications' TipInfo). */
export interface TipReceiptFields {
  isToken: boolean;
  currency?: string;
  category?: string;
  amountTokenBase?: bigint;
  amountSats?: number;
  amountUsd?: number;
}

/** A human-readable description of a tip for notifications. */
export interface TipDescription {
  /** e.g. "13 FURU", "$0.25 PUSD", "$0.10", "1,200 sats". */
  amountLabel: string;
  /** Token ticker when it's a token tip, else undefined. */
  symbol?: string;
  /** Token icon URL when resolvable, else undefined. */
  iconUrl?: string;
  /** True for CashToken tips (vs plain BCH). */
  isToken: boolean;
}

function formatWhole(n: number, maxDp: number): string {
  return n.toLocaleString(undefined, { maximumFractionDigits: maxDp });
}

/**
 * Turn a parsed tip receipt into a display description for notifications.
 * Resolves token decimals/icon from the allowlist when available; degrades
 * gracefully (shows base units, no icon) for unknown tokens or before the
 * allowlist has resolved. Pure/synchronous — safe to call during render.
 */
export function describeTip(tip: TipReceiptFields): TipDescription {
  if (tip.isToken) {
    const sym = tip.currency || 'token';
    const token = (tip.category ? findTipTokenByCategory(tip.category) : null) ?? findTipToken(sym);
    const iconUrl = token?.iconUrl || undefined;

    // Stablecoins read best as dollars; others as token units.
    if (tip.amountTokenBase != null && token) {
      const whole = baseUnitsToWhole(tip.amountTokenBase, token.decimals);
      if (token.stable) {
        return { amountLabel: `$${whole.toFixed(2)} ${sym}`, symbol: sym, iconUrl, isToken: true };
      }
      const dp = token.decimals === 0 ? 0 : 4;
      return { amountLabel: `${formatWhole(whole, dp)} ${sym}`, symbol: sym, iconUrl, isToken: true };
    }
    // Unresolved decimals: fall back to USD if the sender recorded it.
    if (tip.amountUsd != null && tip.amountUsd > 0) {
      return { amountLabel: `$${tip.amountUsd < 1 ? tip.amountUsd.toFixed(2) : formatWhole(tip.amountUsd, 2)} ${sym}`, symbol: sym, iconUrl, isToken: true };
    }
    return { amountLabel: sym, symbol: sym, iconUrl, isToken: true };
  }

  // BCH tip — prefer the recorded USD value, else show sats/BCH.
  if (tip.amountUsd != null && tip.amountUsd > 0) {
    return { amountLabel: `$${tip.amountUsd < 1 ? tip.amountUsd.toFixed(2) : formatWhole(tip.amountUsd, 2)}`, isToken: false };
  }
  if (tip.amountSats != null && tip.amountSats > 0) {
    const sats = tip.amountSats;
    if (sats < 1e5) return { amountLabel: `${sats.toLocaleString()} sats`, isToken: false };
    const bch = sats / 1e8;
    return { amountLabel: `${bch.toFixed(bch < 0.01 ? 6 : 4)} BCH`, isToken: false };
  }
  return { amountLabel: 'BCH', isToken: false };
}
